import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import AppsIcon from "@mui/icons-material/Apps";

import { navOptions } from "./AppData";
import { openPopup } from "../../store/actions";

const useStyles = makeStyles()((theme) => ({
  workspaceMenuLink: {
    color: "rgba(0, 0, 0, 0.87)",
    "&:hover": {
      color: "rgba(0, 0, 0, 0.87)",
    },
  },
  menuItem: {
    height: "100%",
    textAlign: "center",
    display: "block",
    borderRadius: "16px",
    padding: "6px",
    width: "85px",
  },
  menuItemIconContainer: {
    padding: "0 0 4px",
    marginLeft: "auto",
    marginRight: "auto",
    color: "#0AB2B2",
  },
  menuItemTextContainer: {
    display: "block",
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
  },
  menuContainer: {
    maxHeight: "450px",
    maxWidth: "280px",
    overflowY: "auto",
    borderRadius: "16px",
    // border: "5px solid #e8edf1",
    "&::-webkit-scrollbar": {
      width: "5px",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "10px",
      marginTop: "12px",
      marginBottom: "12px",
    },
    "& .MuiList-root": {
      padding: "6px",
    },
  },
  gridContainer: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "8px",
  },
  menu: {
    "& .MuiList-root": {
      padding: "0px",
    },
  },
}));

const WorkspaceMenuItem = ({ link, icon, name, handlePopupMenuClick, popup }) => {
  const { classes } = useStyles();
  const { Workspace_id } = useParams();

  return (
    <MenuItem className={classes.menuItem}>
      {link ? (
        <Link to={`/my-workspaces/${Workspace_id}/${link}`} className={classes.workspaceMenuLink}>
          <Box className={classes.menuItemIconContainer}>{icon}</Box>
          <Typography variant="caption" className={classes.menuItemTextContainer} fontWeight={600}>
            {name}
          </Typography>
        </Link>
      ) : (
        <>
          <Box className={classes.menuItemIconContainer}>{icon}</Box>
          <Typography variant="caption" className={classes.menuItemTextContainer} fontWeight={600}>
            {name}
          </Typography>
        </>
      )}
    </MenuItem>
  );
};

export default function AccountMenu() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  let prevGroup = null;

  const workspace = useSelector((state) => state.workspace);
  const user = useSelector((state) => state.user);

  const { connections = [], quickImportAllowed = false } = useMemo(() => workspace, [workspace]);
  const { Roles = [] } = useMemo(() => user.currentUser, [user.currentUser]);

  const $datascienceAllowed = useMemo(
    () =>
      (connections.find((connection) => connection.Type === "Jupyter") || false) &&
      (Roles.includes("DataManager") || Roles.includes("DataAnalyst") || Roles.includes("BusinessAdmin")),
    [Roles, connections]
  );

  const $sageMakerAllowed = useMemo(
    () => connections.find((connection) => connection.Type === "SageMaker") || false,
    [connections]
  );

  const $navOptions = useMemo(() => {
    const optionsToFilter = [];
    if (!quickImportAllowed) optionsToFilter.push("Quick-Import");
    if (!$datascienceAllowed) optionsToFilter.push("Data-Science");
    if (!$sageMakerAllowed) optionsToFilter.push("Machine-Learning");

    return [...navOptions.filter(({ id }) => !optionsToFilter.includes(id))];
  }, [quickImportAllowed, $datascienceAllowed, $sageMakerAllowed]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePopupMenuClick = (popup) => {
    handleClose();
    dispatch(openPopup({ [popup]: true }));
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          paddingLeft: "auto",
        }}
      >
        <Tooltip title="Workspace Menu" arrow>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-controls={open ? "Workspace-Menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <AppsIcon sx={{ width: 28, height: 28 }} className="ninedot" color="secondary" />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="Workspace-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{ paper: { style: { borderRadius: "16px", backgroundColor: "#e8edf1" } } }}
        sx={{ marginTop: "4px" }}
        className={classes.menu}
      >
        <Box className={classes.menuContainer}>
          <Grid container className={classes.gridContainer} alignItems="stretch" spacing={1}>
            {$navOptions.map(({ id, name, group, icon, link, popup }, index) => {
              const isDifferentGroup = prevGroup !== group && index !== 0;
              prevGroup = group;

              return (
                <React.Fragment key={id}>
                  {isDifferentGroup && (
                    <Grid
                      sx={{ height: "4px", backgroundColor: "#e8edf1", marginTop: "8px" }}
                      size={12}></Grid>
                  )}
                  <Grid size={4}>
                    <WorkspaceMenuItem
                      link={link}
                      icon={icon}
                      name={name}
                      handlePopupMenuClick={handlePopupMenuClick}
                      popup={popup}
                    />
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        </Box>
      </Menu>
    </React.Fragment>
  );
}
